import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

/* ───────────────────────── colour palette ───────────────────────── */
const C = {
  frontend:  { bg: '#1a1a2e', border: '#e94560', text: '#e94560' },
  container: { bg: '#1a1a2e', border: '#0f3460', text: '#4ea8de' },
  api:       { bg: '#1a1a2e', border: '#533483', text: '#b388ff' },
  db:        { bg: '#1a1a2e', border: '#1b998b', text: '#1b998b' },
  job:       { bg: '#1a1a2e', border: '#f77f00', text: '#f77f00' },
  external:  { bg: '#1a1a2e', border: '#00b4d8', text: '#00b4d8' },
  ios:       { bg: '#1a1a2e', border: '#06d6a0', text: '#06d6a0' },
  group:     { bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.08)' },
};

/* ───────────────────────── custom node ───────────────────────── */
function SystemNode({ data }) {
  const { label, desc, items, color, icon, collapsed: defaultCollapsed, forceCollapsed } = data;
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false);

  useEffect(() => {
    if (forceCollapsed !== undefined) setCollapsed(forceCollapsed);
  }, [forceCollapsed]);

  return (
    <div
      style={{
        background: color.bg,
        border: `2px solid ${color.border}`,
        borderRadius: 12,
        padding: '10px 14px',
        minWidth: items ? 200 : 140,
        maxWidth: 280,
        fontFamily: "'Inter', -apple-system, sans-serif",
        position: 'relative',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: color.border, width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right} style={{ background: color.border, width: 8, height: 8 }} />
      <Handle type="target" position={Position.Top} id="top" style={{ background: color.border, width: 8, height: 8 }} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ background: color.border, width: 8, height: 8 }} />

      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, cursor: items ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        onClick={() => items && setCollapsed(!collapsed)}
      >
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
        <span style={{ color: color.text, fontWeight: 700, fontSize: 13, letterSpacing: 0.3 }}>
          {label}
        </span>
        {items && (
          <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginLeft: 'auto' }}>
            {collapsed ? `+${items.length}` : '−'}
          </span>
        )}
      </div>

      {desc && (
        <div style={{
          color: 'rgba(255,255,255,0.45)',
          fontSize: 10,
          marginTop: 4,
          lineHeight: 1.4,
          fontStyle: 'italic',
        }}>
          {desc}
        </div>
      )}

      {items && !collapsed && (
        <div style={{ marginTop: 8, borderTop: `1px solid ${color.border}44`, paddingTop: 6 }}>
          {items.map((item, i) => (
            <div
              key={i}
              style={{
                color: 'rgba(255,255,255,0.65)',
                fontSize: 11,
                padding: '2px 0',
                fontFamily: "'JetBrains Mono', monospace",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const nodeTypes = { system: SystemNode };

/* ───────────────────────── node definitions ───────────────────────── */
function buildNodes() {
  const nodes = [];
  let id = 0;
  const n = (label, x, y, color, icon, items, opts = {}) => {
    nodes.push({
      id: `n${id}`,
      type: 'system',
      position: { x, y },
      data: { label, items, color, icon, ...opts },
      ...opts.extra,
    });
    return `n${id++}`;
  };

  /* ── Users / Clients ── */
  const browser   = n('Browser Users', 0, 450, C.frontend, '🌐', null, { desc: 'Visitors accessing the site via web browser' });
  const iosApp    = n('PWHL iOS App', 0, 580, C.ios, '📱', [
    'pwhl.unsupervisedbias.com',
    '/api/v1/pwhl/*',
    '/api/v1/trends',
  ], { desc: 'Native iOS app for PWHL hockey fans' });

  /* ── Infrastructure (Docker Containers) ── */
  const nginx     = n('Nginx', 350, 450, C.container, '🔀', [
    'Ports: 80, 443',
    'SSL via Certbot',
    'Reverse proxy → :8000',
    'Serves React build',
    'Serves static/media',
  ], { desc: 'Reverse proxy and static file server' });
  const certbot   = n('Certbot', 350, 700, C.container, '🔒', ['Auto-renew 12h'], { desc: 'Automated Let\'s Encrypt SSL certificates' });

  const backend   = n('Django + Gunicorn', 700, 0, C.container, '🐍', [
    'Port: 8000',
    '4 workers (prod)',
    'JWT authentication',
    'DRF + Swagger docs',
  ], { desc: 'Main application server handling all API requests' });

  const celeryW   = n('Celery Worker', 700, 560, C.job, '⚙️', [
    'Background tasks',
    'Newsletter generation',
    'Trading operations',
  ], { desc: 'Processes async tasks dispatched via Redis' });
  const celeryB   = n('Celery Beat', 700, 780, C.job, '🕐', [
    'weekly-newsletter (Sun 8am)',
    'daily-monitoring (10am M-F)',
    'monthly-rebalance (1st)',
    'check-limit-orders (15m)',
    'sync-positions (1m M-F mkt hrs)',
    'daily-snapshots (4:30pm)',
    'congress-trades (30m M-F mkt hrs)',
  ], { desc: 'Cron-like scheduler for recurring tasks' });

  const redis     = n('Redis', 700, 1060, C.container, '🔴', [
    'Port: 6379',
    'Celery broker',
    'Result backend',
  ], { desc: 'In-memory message broker between Beat and Worker' });

  const postgres  = n('PostgreSQL 14', 1100, 700, C.db, '🗄️', [
    'Database: unsupervisedbias',
  ], { desc: 'Primary relational database for all app data' });

  /* ── API Endpoint Groups ── */
  const apiAcct   = n('Accounts API', 1100, 0, C.api, '👤', [
    'POST /api/accounts/token/',
    'POST /api/accounts/register/',
    'GET  /api/accounts/profile/',
    'POST /api/accounts/verify-email/',
    'POST /api/accounts/password-reset/',
    '/api/accounts/admin/inspect/*',
    '/api/accounts/admin/inspect/chat-sessions/',
  ], { desc: 'User auth, registration, profiles, and admin inspection' });

  const apiBlog   = n('Blog API', 1100, 250, C.api, '📝', [
    'GET  /api/blog/posts/',
    'POST /api/blog/posts/',
    'POST .../submit-for-review/',
    'POST .../approve/ | reject/',
    '/api/blog/categories/',
    '/api/blog/tags/',
    '/api/blog/comments/',
  ], { desc: 'Blog CRUD, moderation workflow, comments, and likes' });

  const apiCore   = n('Core API', 1100, 520, C.api, '🏠', [
    'GET /api/core/services/',
    'GET /api/core/testimonials/',
    'GET /api/core/faqs/',
    'POST /api/core/contact/',
    '/api/core/cart/',
    'POST /api/core/chatbot/message/',
    'POST /api/core/chatbot/feedback/',
    'POST /api/core/search/ai/',
    'GET /api/core/gallery/{collection}/',
    'POST /api/core/gallery/{collection}/',
    'GET /api/core/gallery/{collection}/{id}/download/',
    'GET /api/core/gallery/{collection}/download-all/',
  ], { desc: 'Services, FAQs, contact form, cart, chatbot, AI search, and photo galleries' });

  const apiTrade  = n('Trading API', 1100, 790, C.api, '📈', [
    'GET /api/trading/dashboard/',
    'GET /api/trading/positions/',
    'GET /api/trading/orders/',
    'GET /api/trading/trades/',
    'GET /api/trading/performance/',
    'GET /api/trading/screening/',
    'GET /api/trading/alerts/',
    'GET /api/trading/logs/',
    'GET /api/trading/stock-lookup/',
    'POST /api/trading/sync/',
    'POST /api/trading/rebalance/',
    'GET /api/trading/congress-trades/',
  ], { desc: 'Algorithmic trading dashboard, positions, and alerts' });

  const apiMsg    = n('Messaging API', 1100, 1140, C.api, '💬', [
    'GET /api/messaging/conversations/',
    'POST /api/messaging/messages/',
  ], { desc: 'User-to-staff support conversations' });

  const apiAnalyt = n('Analytics API', 1100, 1320, C.api, '📊', [
    'POST /api/analytics/beacon/',
    'GET  /api/analytics/stats/',
  ], { desc: 'Page view tracking and visitor analytics' });

  /* ── Database Tables ── */
  const dbAcct    = n('Account Tables', 1500, 0, C.db, '', [
    'accounts_user',
    'accounts_useremail',
  ], { desc: 'Users, credentials, email verification' });
  const dbBlog    = n('Blog Tables', 1500, 170, C.db, '', [
    'blog_blogpost',
    'blog_category',
    'blog_tag',
    'blog_comment',
    'blog_commentlike',
    'blog_blogimage',
  ], { desc: 'Posts, categories, tags, comments, and images' });
  const dbCore    = n('Core Tables', 1500, 420, C.db, '', [
    'core_service',
    'core_testimonial',
    'core_client',
    'core_faq',
    'core_contactsubmission',
    'core_cart / core_cartitem',
    'core_chatsession',
    'core_chatmessage',
    'core_galleryimage',
  ], { desc: 'Business content: services, testimonials, cart, chat history, galleries' });
  const dbTrade   = n('Trading Tables', 1500, 670, C.db, '', [
    'trading_accounts',
    'trading_positions',
    'trading_orders',
    'trading_trades',
    'trading_screening_results',
    'trading_performance_snapshots',
    'trading_alerts',
    'trading_system_logs',
  ], { desc: 'Alpaca accounts, positions, orders, and performance' });
  const dbMsg     = n('Messaging Tables', 1500, 970, C.db, '', [
    'messaging_conversation',
    'messaging_message',
  ], { desc: 'Support threads and individual messages' });
  const dbOther   = n('Other Tables', 1500, 1140, C.db, '', [
    'analytics_visitorlog',
    'scheduling_appointment',
    'scheduling_availabilityslot',
    'documents_document',
    'documents_documentshare',
    'payments_payment',
    'payments_invoice',
    'payments_invoiceitem',
    'verification_codes',
  ], { desc: 'Analytics, scheduling, documents, payments, verification codes' });

  /* ── Frontend Pages ── */
  const pgPublic  = n('Public Pages', -350, 0, C.frontend, '📄', [
    '/ (Home)',
    '/about',
    '/services',
    '/blog  /blog/:slug',
    '/blog/create  /blog/edit/:slug',
    '/trends/:date/:hash',
    '/authors',
    '/contact',
    '/faq',
    '/camels',
    '/wharf',
    '/terms-of-service',
    '/privacy-policy',
  ], { desc: 'Accessible to all visitors without login' });

  const pgAuth    = n('Auth Pages', -350, 330, C.frontend, '🔐', [
    '/login  /signin  /register',
    '/create-account',
    '/registration-success',
    '/verify-email/:token',
    '/verify-additional-email',
    '/forgot-password',
    '/reset-password/:uid/:token',
  ], { desc: 'Login, registration, email verification, password reset' });

  const pgUser    = n('User Pages', -350, 590, C.frontend, '👤', [
    '/profile',
    '/settings',
    '/my-blogs',
    '/my-services',
    '/messages',
    '/cart',
    '/book-call',
  ], { desc: 'Authenticated user dashboard and account management' });

  const pgAdmin   = n('Admin Pages', -350, 840, C.frontend, '🛡️', [
    '/admin/analytics',
    '/admin/blog-review',
    '/admin/database',
    '/admin/jobs',
    '/admin/architecture ← you are here',
  ], { desc: 'Staff-only tools for monitoring and management' });

  const pgTrade   = n('Trading Pages', -350, 1070, C.frontend, '📊', [
    '/trading/dashboard',
    '/trading/positions',
    '/trading/orders',
    '/apps',
  ], { desc: 'Algorithmic trading interface powered by Alpaca' });

  /* ── External Services ── */
  const anthropic = n('Anthropic (Claude)', 700, 1500, C.external, '🤖', [
    'claude-sonnet-4-6 (Claude Code)',
    'claude-opus-4-5 (newsletter/articles)',
    'Newsletter content',
    'PWHL article generation',
  ], { desc: 'Primary LLM for newsletter, article generation, and Claude Code sessions' });
  const openai    = n('Azure OpenAI', 350, 1500, C.external, '🧠', [
    'Embeddings + vector search',
    'ChromaDB vector store',
  ], { desc: 'Embeddings for RAG knowledge base (chatbot uses Claude)' });
  const ollama    = n('Ollama (Local)', 1100, 1500, C.external, '🦙', [
    'llama3.2:3b',
    'Newsletter fallback',
    'Runs on host machine',
  ], { desc: 'Local LLM fallback when Claude API is unavailable' });
  const alpaca    = n('Alpaca API', 1500, 1500, C.external, '🦙', [
    'Brokerage API',
    'Paper & live trading',
    'Market data + orders',
  ], { desc: 'Stock brokerage for automated value-momentum trading' });
  const gmail     = n('Gmail SMTP', 0, 1500, C.external, '📧', [
    'smtp.gmail.com:587',
    'Verification emails',
    'Newsletters',
    'Password resets',
  ], { desc: 'Outbound email via Google Workspace SMTP' });

  const rss       = n('RSS / Web Sources', 350, 1740, C.external, '📡', [
    'VentureBeat, TechCrunch',
    'MIT Tech Review, The Verge',
    'OpenAI, HuggingFace, etc.',
    'Anthropic & Ultralytics blogs',
  ], { desc: '11 RSS feeds + 2 scraped blogs for weekly newsletter' });

  const stripe    = n('Stripe', 700, 1740, C.external, '💳', ['Payments (planned)'], { desc: 'Payment processing for services (not yet active)' });
  const calcom    = n('Cal.com', 1100, 1740, C.external, '📅', ['Scheduling (planned)'], { desc: 'Meeting booking integration (not yet active)' });
  const pwhlApi   = n('PWHL API', 0, 800, C.external, '🏒', [
    'pwhl.unsupervisedbias.com',
    'FastAPI + PostgreSQL (Docker)',
    '/api/v1/players  /api/v1/pwhl/*',
    '/api/v1/trends  /api/v1/prospects',
    'HockeyTech scraper (daily 6am)',
    'APScheduler newsletter (Sun 9am)',
    'pdubfantasy.com frontend',
    'PWHL iOS app backend',
  ], { desc: 'Separate FastAPI service: player stats, trends, prospects, fantasy scoring, weekly newsletter' });

  return {
    nodes,
    ids: {
      browser, iosApp, nginx, certbot, backend, celeryW, celeryB, redis, postgres,
      apiAcct, apiBlog, apiCore, apiTrade, apiMsg, apiAnalyt,
      dbAcct, dbBlog, dbCore, dbTrade, dbMsg, dbOther,
      pgPublic, pgAuth, pgUser, pgAdmin, pgTrade,
      anthropic, openai, ollama, alpaca, gmail, rss, stripe, calcom, pwhlApi,
    },
  };
}

/* ───────────────────────── edge definitions ───────────────────────── */
function buildEdges(ids) {
  const e = (source, target, label, opts = {}) => ({
    id: `e-${source}-${target}-${label || ''}`,
    source, target, label,
    type: 'smoothstep',
    animated: opts.animated ?? false,
    style: { stroke: opts.color || 'rgba(255,255,255,0.15)', strokeWidth: 1.5 },
    labelStyle: { fill: 'rgba(255,255,255,0.5)', fontSize: 10, fontFamily: 'Inter, sans-serif' },
    labelBgStyle: { fill: '#0d1117', fillOpacity: 0.9 },
    markerEnd: { type: MarkerType.ArrowClosed, color: opts.color || 'rgba(255,255,255,0.3)', width: 14, height: 14 },
    zIndex: 10,
    ...(opts.sourceHandle ? { sourceHandle: opts.sourceHandle } : {}),
    ...(opts.targetHandle ? { targetHandle: opts.targetHandle } : {}),
    ...opts.extra,
  });

  return [
    // Users → Nginx
    e(ids.browser, ids.nginx, 'HTTPS', { color: '#e94560' }),
    e(ids.iosApp, ids.pwhlApi, 'REST API', { color: '#06d6a0' }),
    e(ids.pwhlApi, ids.nginx, '', { color: '#06d6a0' }),

    // Nginx → Backend (bottom→top to go up cleanly)
    e(ids.nginx, ids.backend, '/api/* proxy', { color: '#4ea8de', animated: true, sourceHandle: 'top', targetHandle: 'top' }),
    e(ids.certbot, ids.nginx, 'SSL certs', { color: '#4ea8de', sourceHandle: 'top', targetHandle: 'bottom' }),

    // Frontend pages → Browser
    e(ids.pgPublic, ids.browser, '', { color: '#e94560' }),
    e(ids.pgAuth, ids.browser, '', { color: '#e94560' }),
    e(ids.pgUser, ids.browser, '', { color: '#e94560' }),
    e(ids.pgAdmin, ids.browser, '', { color: '#e94560' }),
    e(ids.pgTrade, ids.browser, '', { color: '#e94560' }),

    // Backend → API groups
    e(ids.backend, ids.apiAcct, '', { color: '#b388ff' }),
    e(ids.backend, ids.apiBlog, '', { color: '#b388ff', sourceHandle: 'bottom' }),
    e(ids.backend, ids.apiCore, '', { color: '#b388ff', sourceHandle: 'bottom' }),
    e(ids.backend, ids.apiTrade, '', { color: '#b388ff', sourceHandle: 'bottom' }),
    e(ids.backend, ids.apiMsg, '', { color: '#b388ff', sourceHandle: 'bottom' }),
    e(ids.backend, ids.apiAnalyt, '', { color: '#b388ff', sourceHandle: 'bottom' }),

    // APIs → DB tables (right→left, same row)
    e(ids.apiAcct, ids.dbAcct, '', { color: '#1b998b' }),
    e(ids.apiBlog, ids.dbBlog, '', { color: '#1b998b' }),
    e(ids.apiCore, ids.dbCore, '', { color: '#1b998b' }),
    e(ids.apiTrade, ids.dbTrade, '', { color: '#1b998b' }),
    e(ids.apiMsg, ids.dbMsg, '', { color: '#1b998b' }),
    e(ids.apiAnalyt, ids.dbOther, '', { color: '#1b998b' }),

    // Postgres → DB tables (use top/bottom to fan out vertically)
    e(ids.postgres, ids.dbAcct, '', { color: '#1b998b', sourceHandle: 'top', targetHandle: 'bottom' }),
    e(ids.postgres, ids.dbBlog, '', { color: '#1b998b', sourceHandle: 'top', targetHandle: 'bottom' }),
    e(ids.postgres, ids.dbCore, '', { color: '#1b998b' }),
    e(ids.postgres, ids.dbTrade, '', { color: '#1b998b' }),
    e(ids.postgres, ids.dbMsg, '', { color: '#1b998b', sourceHandle: 'bottom' }),
    e(ids.postgres, ids.dbOther, '', { color: '#1b998b', sourceHandle: 'bottom', targetHandle: 'top' }),

    // Celery (vertical chain)
    e(ids.celeryB, ids.redis, 'schedule', { color: '#f77f00', animated: true, sourceHandle: 'bottom', targetHandle: 'top' }),
    e(ids.redis, ids.celeryW, 'tasks', { color: '#f77f00', animated: true, sourceHandle: 'top', targetHandle: 'bottom' }),
    e(ids.celeryW, ids.postgres, 'read/write', { color: '#f77f00' }),
    e(ids.backend, ids.redis, 'dispatch tasks', { color: '#f77f00', sourceHandle: 'bottom' }),

    // External services (use bottom handles to route down cleanly)
    e(ids.celeryW, ids.anthropic, 'newsletter gen', { color: '#00b4d8', animated: true, sourceHandle: 'bottom' }),
    e(ids.celeryW, ids.ollama, 'fallback LLM', { color: '#00b4d8', sourceHandle: 'bottom' }),
    e(ids.celeryW, ids.alpaca, 'trading ops', { color: '#00b4d8', animated: true, sourceHandle: 'bottom' }),
    e(ids.celeryW, ids.gmail, 'send emails', { color: '#00b4d8', sourceHandle: 'bottom' }),
    e(ids.celeryW, ids.rss, 'fetch news', { color: '#00b4d8', sourceHandle: 'bottom' }),
    e(ids.backend, ids.openai, 'chatbot RAG', { color: '#00b4d8', sourceHandle: 'bottom' }),
    e(ids.backend, ids.gmail, 'transactional', { color: '#00b4d8', sourceHandle: 'bottom' }),
    e(ids.backend, ids.stripe, 'payments', { color: '#00b4d8', sourceHandle: 'bottom' }),
    e(ids.backend, ids.calcom, 'scheduling', { color: '#00b4d8', sourceHandle: 'bottom' }),
  ];
}

/* ───────────────────────── legend ───────────────────────── */
function Legend({ allCollapsed, onToggleAll }) {
  const items = [
    { color: C.frontend.border, label: 'Frontend / Users' },
    { color: C.container.border, label: 'Docker Containers' },
    { color: C.api.border, label: 'API Endpoints' },
    { color: C.db.border, label: 'Database Tables' },
    { color: C.job.border, label: 'Background Jobs' },
    { color: C.external.border, label: 'External Services' },
    { color: C.ios.border, label: 'iOS App / PWHL' },
  ];

  return (
    <div style={{
      position: 'absolute', top: 14, left: 14, zIndex: 10,
      background: 'rgba(13,17,23,0.92)', borderRadius: 10,
      padding: '12px 16px', border: '1px solid rgba(255,255,255,0.08)',
      backdropFilter: 'blur(12px)',
    }}>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, marginBottom: 8, letterSpacing: 0.5 }}>
        System Architecture
      </div>
      {items.map(({ color, label }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{label}</span>
        </div>
      ))}
      <button
        onClick={onToggleAll}
        style={{
          marginTop: 8,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: 8,
          width: '100%',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6,
          color: 'rgba(255,255,255,0.6)',
          fontSize: 11,
          padding: '5px 10px',
          cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {allCollapsed ? 'Expand All' : 'Collapse All'}
      </button>
      <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, marginTop: 6 }}>
        Click nodes to expand details
      </div>
    </div>
  );
}

/* ───────────────────────── main component ───────────────────────── */
export default function AdminArchitecture() {
  const { user } = useAuth();
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [forceSignal, setForceSignal] = useState(0);

  const { initialNodes, initialEdges } = useMemo(() => {
    const { nodes: initialNodes, ids } = buildNodes();
    const initialEdges = buildEdges(ids);
    return { initialNodes, initialEdges };
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  const handleToggleAll = useCallback(() => {
    const newCollapsed = !allCollapsed;
    setAllCollapsed(newCollapsed);
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: { ...node.data, forceCollapsed: newCollapsed },
      }))
    );
  }, [allCollapsed, setNodes]);

  const onInit = useCallback((instance) => {
    setTimeout(() => instance.fitView({ padding: 0.12 }), 100);
  }, []);

  if (!user?.is_staff) return <Navigate to="/" />;

  return (
    <div style={{
      width: '100%',
      height: 'calc(100vh - 80px)',
      background: '#0d1117',
      position: 'relative',
    }}>
      <Legend allCollapsed={allCollapsed} onToggleAll={handleToggleAll} />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onInit={onInit}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.15}
        maxZoom={2.5}
        defaultEdgeOptions={{ type: 'smoothstep' }}
        proOptions={{ hideAttribution: true }}
        style={{ background: '#0d1117' }}
      >
        <Controls
          position="bottom-right"
          style={{ background: '#1a1a2e', borderColor: 'rgba(255,255,255,0.1)' }}
        />
        <MiniMap
          position="bottom-left"
          nodeColor={(node) => node.data?.color?.border || '#555'}
          maskColor="rgba(13,17,23,0.85)"
          style={{ background: '#0d1117', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}
        />
        <Background color="rgba(255,255,255,0.03)" gap={24} />
      </ReactFlow>
    </div>
  );
}
